import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import moment from 'moment';
import { MessageField } from 'Src/models/message';
import { FormRendererV2 } from 'Src/components/FormRendererV2/FormRendererV2';
import { FormDefinition } from 'Src/types/formSchema';
import { FormSubmission } from 'Src/types/formSchema';
import {
  applyAnswersToLegacyFields,
  applyLegacyRequiredFlagsToDefinition,
  formDefinitionToLegacyFields,
  legacyFieldsToAnswers,
  legacyFieldsToFormDefinition
} from 'Src/helpers/form/legacyFormAdapter';
import {
  FormAnswerMap,
  FormErrorMap,
  answerMapToArray,
  answersToMap,
  validateFormDefinition
} from 'Src/helpers/form/formRuntime';
import { getSubmissionElementIds, hasFormFlow } from 'Src/helpers/form/formFlowRuntime';
import { uploadFormFilesForElement } from 'Src/helpers/form/formMedia';
import { EventInvitationMessage } from '../../../../../models/message';
import { Map } from 'Src/components/Map/Map';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from '../../../../../consts/common';
import './ParentalConsentRequestForm.scss';
import { VENUE_SERVER_TO_CLIENT_MAPPING } from '../../../../../consts/venue';
import { AppUser } from 'Src/views/App/App';
import { formatAgesCompactForSchool } from 'Src/helpers/ageGroups';

interface Props {
  user: AppUser;
  message: EventInvitationMessage;
  onDeclineClick: () => void;
  onAcceptClick: (fields: MessageField[], submission?: FormSubmission) => void;
  closeConsentRequestModal: () => void;
  eventName?: string;
}

interface State {
  definition: FormDefinition;
  answers: FormAnswerMap;
  isMapVisible: boolean;
  errors: FormErrorMap;
}

export class ParentalConsentRequestForm extends Component<Props, State> {
  constructor(props) {
    super(props);
    const { message } = this.props;
    const fields = (propz.get(message, ['fields'], []) || []).map(field => ({ ...field }));
    const isV2 = message.formSchemaVersion === 2 && !!message.formDefinition;
    const definition = isV2
      ? applyLegacyRequiredFlagsToDefinition(message.formDefinition!, fields)
      : legacyFieldsToFormDefinition(fields);

    this.state = {
      definition,
      answers: answersToMap(isV2 ? message.formAnswers || [] : legacyFieldsToAnswers(fields, definition)),
      isMapVisible: false,
      errors: {}
    };
  }

  getAges(): string {
    const { message, user } = this.props;
    const ages = propz.get(message, ['eventData', 'ages']);
    if (typeof ages === 'undefined') return '';

    return formatAgesCompactForSchool(ages, propz.get(message, ['schoolData'], user.activeSchool));
  }

  getVenue(): string {
    const { message } = this.props;
    const postcode = propz.get(message, ['eventData', 'venue', 'postcodeData', 'postcode'], '');
    const placeName = propz.get(
      message,
      ['eventData', 'venue', 'placeData', 'name'],
      VENUE_SERVER_TO_CLIENT_MAPPING.HOME
    );
    const venueType = propz.get(message, ['eventData', 'venue', 'venueType'], VENUE_SERVER_TO_CLIENT_MAPPING.TBD);

    switch (true) {
      case venueType === VENUE_SERVER_TO_CLIENT_MAPPING.TBD:
        return 'TBD';
      case typeof placeName !== 'undefined':
        return `${placeName}, ${postcode}`;
      default:
        return postcode;
    }
  }

  onSubmit = () => {
    const { definition, answers } = this.state;
    const errors = validateFormDefinition(definition, answers, getSubmissionElementIds(definition, answers));

    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    const isV2 = this.props.message.formSchemaVersion === 2 && !!this.props.message.formDefinition;
    const fields = isV2
      ? formDefinitionToLegacyFields(definition)
      : (this.props.message.fields || []).map(field => ({ ...field }));
    const fieldsWithAnswers = applyAnswersToLegacyFields(
      fields,
      definition,
      answerMapToArray(answers)
    ) as MessageField[];

    this.props.onAcceptClick(
      fieldsWithAnswers,
      isV2
        ? {
            formSchemaVersion: 2,
            formAnswers: answerMapToArray(answers)
          }
        : undefined
    );
  };

  onAnswersChange = (answers: FormAnswerMap) => {
    this.setState(prev => {
      const errors = { ...prev.errors };
      Object.keys(answers).forEach(id => {
        if (JSON.stringify(answers[id]) !== JSON.stringify(prev.answers[id])) {
          delete errors[id];
        }
      });
      return { answers, errors };
    });
  };

  toggleMap = () => {
    const { isMapVisible } = this.state;
    this.setState({
      isMapVisible: !isMapVisible
    });
  };

  render() {
    const { message, eventName } = this.props;
    const { definition, answers, isMapVisible, errors } = this.state;
    const flowEnabled = hasFormFlow(definition);

    const isFieldsExist = definition.elements.length > 0;
    const firstName = propz.get(message, ['playerDetailsData', 'firstName'], '');
    const lastName = propz.get(message, ['playerDetailsData', 'lastName'], '');
    const fullName = `${firstName} ${lastName}`;
    const sport = propz.get(message, ['eventData', 'sport', 'name'], '');
    const startTime = propz.get(message, ['eventData', 'startTime'], '');
    const startTimeFormatted = moment(startTime).format('DD.MM.YYYY / HH:mm');
    const postcode = this.getVenue();
    const point = propz.get(message, ['eventData', 'venue', 'point']);
    const gender = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[propz.get(message, ['eventData', 'gender'])];
    const deadlineForAnswers = propz.get(message, ['deadlineForAnswers']);
    const deadlineForAnswersFormatted = moment(deadlineForAnswers).format('DD.MM.YYYY / HH:mm');
    const isDeadlineExist = typeof deadlineForAnswers !== 'undefined';
    const now = new Date();
    const isDeadlineArrived = Number(new Date(deadlineForAnswers)) < Number(now);
    const ages = this.getAges();

    const picUrl = propz.get(message, ['schoolData', 'pic'], '');
    const isSchoolLogoExist = picUrl !== '';

    return (
      <>
        <div className={'eConsentRequestFormRow'}>
          <img
            src={isSchoolLogoExist ? picUrl : '/dist/images/no-image.jpg'}
            alt="school logo"
            className={'bConsentRequestFormLogo'}
          />

          <div className="pl-4">
            <div className={'mConsentRequestFormBold'}>{`${eventName}`}</div>

            <div className={'mConsentRequestFormBold'}>{`${sport}`}</div>

            <div>
              <span className={'mConsentRequestFormBold'}>{'Years:'} </span>
              {ages}
            </div>

            <div>
              <span className={'mConsentRequestFormBold'}>{'Gender:'} </span>
              {gender}
            </div>

            <div>
              <span className={'mConsentRequestFormBold'}>{'Start time:'} </span>
              {startTimeFormatted}
            </div>

            <div>
              <span className={'mConsentRequestFormBold'}>{'Venue:'} </span>
              {postcode}
            </div>
          </div>
        </div>

        <div className={'mConsentRequestFormBold'}>
          {`Do you give your consent for ${fullName} to take part in this fixture?`}
        </div>

        {isFieldsExist && (
          <FormRendererV2
            definition={definition}
            answers={answers}
            errors={errors}
            onChange={this.onAnswersChange}
            onSubmitRequested={() => this.onSubmit()}
            uploadFiles={(files, element) => uploadFormFilesForElement(this.props.user, files, element)}
          />
        )}

        <div className="eClubInvitationFormRow">
          <img src={'/dist/images/icons/pin.png'} alt="pin icon" className={'bConsentRequestFormIcon'} />
          <div className="bConsentRequestFormMap" onClick={this.toggleMap}>
            View map
          </div>
        </div>

        {isMapVisible && <Map point={point} customStylingClass="mb-3 eClubWizardMap" isMarkerDraggable={false} />}

        {isDeadlineExist && isDeadlineArrived && (
          <div className="eConsentRequestFormRow">
            <span className="mConsentRequestFormBold">Unfortunately, booking for this activity has now closed.</span>
          </div>
        )}

        {isDeadlineExist && (
          <div className="eConsentRequestFormRow">{`Deadline for answers: ${deadlineForAnswersFormatted}`}</div>
        )}

        <div className="eConsentRequestFormActions">
          <button onClick={this.props.closeConsentRequestModal} className="eConsentRequestFormBtn-cancel">
            Cancel
          </button>
          {!(isDeadlineExist && isDeadlineArrived) && (
            <>
              <button onClick={this.props.onDeclineClick} className="eConsentRequestFormBtn-decline">
                Decline
              </button>
              {!flowEnabled && (
                <button onClick={this.onSubmit} className="eConsentRequestFormBtn-accept">
                  Accept
                </button>
              )}
            </>
          )}
        </div>
      </>
    );
  }
}



